<script setup lang="ts">
import { computed, onMounted, ref } from "vue";
import PayerForm from "#/components/payer/PayerForm.vue";
import RadioCardList from "#/components/form/RadioCardList.vue";
import { employerAccountingApiV1 } from "#/../js/api/client";
import { JuridicalPayer as Payer } from "#/../js/api/v1_employer_accounting/models/juridical-payer";

const loading = ref(true);
const payers = ref<Payer[]>([]);
const selectedPayerId = defineModel({ required: true });
const isFormOpened = ref(false);
const props = withDefaults(defineProps<{ required: boolean }>(), {
  required: false,
});

const payersChoices = computed(() =>
  payers.value.map((p) => {
    return {
      value: p.id,
      title: payerTitle(p),
      badgeTitle: payerTypeTitle(p),
      enabled: true,
    };
  })
);
const selectedPayer = computed(() => {
  for (const payer of payers.value) {
    if (payer.id === selectedPayerId.value) {
      return payer;
    }
  }

  return null;
});

const payerTitle = (payer: Payer) => {
  switch (payer.type) {
    case "juridical":
      return `${payer.companyTitle}, ИНН: ${payer.inn}`;
    default:
      return `Плательщик ${payer.id}`;
  }
};

const payerTypeTitle = (payer: Payer) => {
  switch (payer.type) {
    case "juridical":
      return "Юридическое лицо";
    default:
      return "";
  }
};

const loadPayers = async () => {
  loading.value = true;
  payers.value = (await employerAccountingApiV1.payer.list()).data;
  loading.value = false;
};

onMounted(() => {
  loadPayers();
});
</script>

<template>
  <div class="payer-select">
    <template v-if="loading">
      <div class="loader"></div>
    </template>

    <template v-else>
      <div class="flex justify-between mb-12">
        <label>
          Выберите плательщика
          <span v-if="required" class="red">*</span>
        </label>
        <a
          v-if="payers.length > 0 && !isFormOpened"
          href="javascript:void(0)"
          class="link"
          @click="isFormOpened = true"
        >
          Добавить плательщика
        </a>
      </div>

      <payer-form
        v-if="isFormOpened"
        @payer-created="
          (payer) => {
            isFormOpened = false;
            payers.push(payer);
            selectedPayerId = payer.id;
          }
        "
        @close="isFormOpened = false"
      />

      <template v-else-if="payers.length > 0">
        <radio-card-list
          v-model="selectedPayerId"
          :choices="payersChoices"
          :required="required"
        />
      </template>

      <div v-else class="payer-select__add-btn" @click="isFormOpened = true">
        <i class="raberu-icon-plus-circle"></i>
        <span>Добавить плательщика</span>
      </div>
    </template>
  </div>
</template>

<style scoped lang="scss">
.payer-select {
  .payer-select__add-btn {
    background-color: #ececec;
    color: #636363;
    text-align: center;
    font-size: 14px;
    cursor: pointer;
    vertical-align: middle;
    padding: 15px;
    border-radius: 15px;

    > * {
      vertical-align: middle;
    }

    > i[class^="raberu-icon"] {
      margin-right: 5px;
    }
  }

  a {
    text-decoration: none;

    &:hover {
      text-decoration: underline;
    }
  }
}
</style>
